"use client";

import React from "react";
import { FileText, FileSpreadsheet, File, LucideIcon } from "lucide-react";
import type { ExportOptions } from "./types";

type FormatOption = {
  value: ExportOptions["format"];
  label: string;
  icon: LucideIcon;
  description: string;
};

const FORMAT_OPTIONS: FormatOption[] = [
  { value: "csv", label: "CSV", icon: FileText, description: "Comma-separated values" },
  { value: "xlsx", label: "Excel", icon: FileSpreadsheet, description: "Excel spreadsheet" },
  { value: "pdf", label: "PDF", icon: File, description: "Portable document" },
];

interface ExportFormatPickerProps {
  value: ExportOptions["format"];
  onChange: (format: ExportOptions["format"]) => void;
}

export function ExportFormatPicker({ value, onChange }: ExportFormatPickerProps) {
  return (
    <div>
      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Export Format</h3>
      <div className="grid grid-cols-1 gap-2">
        {FORMAT_OPTIONS.map((option) => {
          const Icon = option.icon;
          const selected = value === option.value;
          return (
            <button type="button"
              key={option.value}
              onClick={() => onChange(option.value)}
              className={`
                flex items-center gap-3 p-3 rounded-lg border text-left transition-colors
                ${
                  selected
                    ? "border-blue-500 bg-blue-50 dark:bg-blue-950/40"
                    : "border-gray-200 dark:border-gray-700 hover:bg-card-50"
                }
              `}
            >
              <Icon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              <div>
                <div className="font-medium text-card-foreground">{option.label}</div>
                <div className="text-xs text-muted-foreground">{option.description}</div>
              </div>
              <div className="ml-auto">
                <div
                  className={`
                    w-4 h-4 rounded-full border-2 transition-colors
                    ${selected ? "border-blue-500 bg-blue-500" : "border-gray-300 dark:border-gray-700"}
                  `}
                >
                  {selected && (
                    <div className="w-full h-full rounded-full bg-card scale-50" />
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
